import { Component } from 'react'
import PropTypes from 'prop-types'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import anime from 'animejs'
import { noop, map, throttle } from 'lodash'
import NavItem from './nav_item'

export default class AbstractNav extends Component {
  static propTypes = {
    items: PropTypes.array,
    section: PropTypes.string,
    navClass: PropTypes.string,
    itemClass: PropTypes.string,
    onNavigate: PropTypes.func,
  }

  static defaultProps = {
    items: [],
    section: null,
    navClass: 'tabbed-nav__nav',
    itemClass: 'tabbed-nav__list-item',
    onNavigate: noop,
  }

  constructor(props) {
    super(props)
    this.state = {
      animating: false,
      canScrollLeft: false,
      canScrollRight: false,
      offset: 0,
    }
    this.animatables = {
      scrollLeft: 0,
    }
    this.animation = null
    this.listeningForResize = false
  }

  handleWindowResize = event => {
    this.handleScroll()
  }
  throttledHandleWindowResize = throttle(this.handleWindowResize, 16)

  componentDidMount() {
    const { canScrollLeft: currentCanScrollLeft, canScrollRight: currentCanScrollRight } = this.state
    const { canScrollLeft, canScrollRight } = this.calculateCanScrollValues()
    if (currentCanScrollLeft !== canScrollLeft || currentCanScrollRight !== canScrollRight) {
      this.setState({
        canScrollLeft,
        canScrollRight,
      })
    }
    this.addWindowResizeListener()
  }

  componentWillUnmount() {
    this.removeWindowResizeListener()
  }

  addWindowResizeListener() {
    if (!this.listeningForResize) {
      window.addEventListener('resize', this.throttledHandleWindowResize)
      this.listeningForResize = true
    }
  }

  removeWindowResizeListener() {
    if (this.listeningForResize) {
      window.removeEventListener('resize', this.throttledHandleWindowResize)
      this.listeningForResize = false
    }
  }

  calcScroll(scrollWidth, offsetWidth, scrollLeft) {
    const diff = scrollWidth - offsetWidth
    let canScrollRight = false
    if (scrollLeft < diff) {
      canScrollRight = true
    }
    const canScrollLeft = scrollLeft > 0
    return { canScrollLeft, canScrollRight }
  }

  handleScroll = offset => {
    const {
      canScrollLeft: currentCanScrollLeft,
      canScrollRight: currentCanScrollRight,
      offset: offsetInState,
    } = this.state
    if (!offset && offset !== 0) {
      offset = offsetInState
    }
    let stateObj = { offset }
    const { canScrollLeft, canScrollRight } = this.calculateCanScrollValues()
    if (currentCanScrollLeft !== canScrollLeft || currentCanScrollRight !== canScrollRight) {
      stateObj = Object.assign(stateObj, {
        canScrollLeft,
        canScrollRight,
      })
    }
    this.animatables.scrollLeft = offset
    this.setState(stateObj)
  }
  throttledHandleScroll = throttle(this.handleScroll, 16)

  handleTabClick = index => {
    const { onNavigate } = this.props
    onNavigate(index)
  }

  handleScrollLeftClick = event => {
    event.preventDefault()
    this.scrollLeft()
  }

  handleScrollRightClick = event => {
    event.preventDefault()
    this.scrollRight()
  }

  scrollLeft() {
    // implement in subclass
  }
  scrollRight() {
    // implement in subclass
  }

  animateTo(offset) {
    this.animation = anime({
      targets: this.animatables,
      scrollLeft: offset,
      easing: 'easeInOutCubic',
      duration: 600,
      update: anim => {
        const { scrollLeft } = this.animatables
        this.setState({
          offset: scrollLeft,
          animating: true,
        })
      },
      complete: () => {
        this.setState({
          animating: false,
        })
      },
    })
  }

  getScrollerContent(direction = 'left') {
    const { canScrollLeft, canScrollRight } = this.state
    let content = null
    if (direction === 'left' && canScrollLeft) {
      content = (
        <a
          key="left-scroller"
          className="tabbed-nav__scroll-indicator scroll-indicator scroll-indicator--left"
          onClick={this.handleScrollLeftClick}
        >
          <div className="arrow">&#10094;</div>
        </a>
      )
    }
    if (direction === 'right' && canScrollRight) {
      content = (
        <a
          key="right-scroller"
          className="tabbed-nav__scroll-indicator scroll-indicator scroll-indicator--right"
          onClick={this.handleScrollRightClick}
        >
          <div className="arrow">&#10095;</div>
        </a>
      )
    }

    if (!content) {
      return null
    }

    const key = `css-transition-${direction}`
    return (
      <CSSTransition key={key} classNames="fade-in-200-out-200" timeout={200}>
        {content}
      </CSSTransition>
    )
  }

  getNavItemsContent() {
    const { items, itemClass, section } = this.props
    return map(items, (item, index) => {
      const { identifier, ...rest } = item
      const key = `nav-item-${index}`
      const active = section === identifier
      return <NavItem key={key} active={active} className={itemClass} {...rest} />
    })
  }

  getScrollersContent() {
    const leftScroller = this.getScrollerContent('left')
    const rightScroller = this.getScrollerContent('right')
    return (
      <TransitionGroup>
        {leftScroller}
        {rightScroller}
      </TransitionGroup>
    )
  }
}
